import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Hero } from '../components/Hero';
import { ProductGrid } from '../components/ProductGrid';
import { ScienceSection } from '../components/ScienceSection';
import { Footer } from '../components/Footer';
import { AboutUs } from '../components/AboutUs';
import { AboutNAD } from '../components/AboutNAD';
import { Services } from '../components/Services';
import { MedicalAesthetics } from '../components/MedicalAesthetics';
import { Book } from '../components/Book';
import { Login } from '../components/Login';
import { Membership } from '../components/Membership';
import { ContactUs } from '../components/ContactUs';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function LuxuryNADPage() {
  const [activeTab, setActiveTab] = useState('home');
  const philosophyAnimation = useScrollAnimation({
    threshold: 0.3
  });
  const experienceAnimation = useScrollAnimation({
    threshold: 0.2
  });
  const ctaAnimation = useScrollAnimation({
    threshold: 0.4
  });
  const renderContent = () => {
    switch (activeTab) {
      case 'about':
        return <AboutUs activeTab={activeTab} onTabChange={setActiveTab} data-id="element-370" />;
      case 'services':
        return <Services data-id="element-371" />;
      case 'aboutnad':
        return <AboutNAD data-id="element-372" />;
      case 'aesthetics':
        return <MedicalAesthetics data-id="element-373" />;
      case 'science':
        return <div className="w-full min-h-screen bg-white pt-32" data-id="element-374">
            <ScienceSection data-id="element-375" />
          </div>;
      case 'membership':
        return <Membership data-id="element-376" />;
      case 'book':
        return <Book data-id="element-377" />;
      case 'contact':
        return <ContactUs data-id="element-378" />;
      case 'login':
        return <Login data-id="element-379" />;
      default:
        return <>
            <Hero activeTab={activeTab} onTabChange={setActiveTab} data-id="element-380" />

            {/* Philosophy Section */}
            <section className="px-6 py-20 md:py-24 bg-zinc-50 text-zinc-950" ref={philosophyAnimation.ref as React.RefObject<HTMLElement>} data-id="element-381">
              <motion.div className="max-w-4xl mx-auto text-center" initial={{
              y: 50,
              opacity: 0
            }} animate={philosophyAnimation.isVisible ? {
              y: 0,
              opacity: 1
            } : {
              y: 50,
              opacity: 0
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} data-id="element-382">
                <p className="text-sm tracking-[0.3em] uppercase mb-6 text-zinc-600" data-id="element-383">
                  Philosophy
                </p>
                <h2 className="text-4xl md:text-6xl font-serif mb-6 leading-tight" style={{
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-384">
                  The future of wellness begins at the cellular level
                </h2>
                <p className="text-lg md:text-xl text-zinc-600 leading-relaxed max-w-2xl mx-auto" data-id="element-385">
                  NAD+ is not merely a supplement. It is the foundation of
                  cellular vitality, the essence of longevity, and the key to
                  unlocking your body's innate potential.
                </p>
              </motion.div>
            </section>

            <ScienceSection data-id="element-386" />
            <ProductGrid data-id="element-387" />

            {/* Experience Section */}
            <section className="px-6 py-32 md:py-40 bg-white" ref={experienceAnimation.ref as React.RefObject<HTMLElement>} data-id="element-388">
              <div className="max-w-7xl mx-auto" data-id="element-389">
                <motion.p className="text-sm tracking-[0.3em] uppercase mb-16 text-center text-zinc-600" initial={{
                y: 30,
                opacity: 0
              }} animate={experienceAnimation.isVisible ? {
                y: 0,
                opacity: 1
              } : {
                y: 30,
                opacity: 0
              }} transition={{
                duration: 0.7,
                ease: 'easeOut'
              }} data-id="element-390">
                  The Experience
                </motion.p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-391">
                  <motion.div className="aspect-[4/5] bg-zinc-200 overflow-hidden group" initial={{
                  x: -40,
                  opacity: 0
                }} animate={experienceAnimation.isVisible ? {
                  x: 0,
                  opacity: 1
                } : {
                  x: -40,
                  opacity: 0
                }} transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: 'easeOut'
                }} data-id="element-392">
                    <motion.img src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80" alt="Morning wellness ritual" className="w-full h-full object-cover" whileHover={{
                    scale: 1.05
                  }} transition={{
                    duration: 0.7,
                    ease: 'easeOut'
                  }} data-id="element-393" />
                  </motion.div>

                  <div className="flex flex-col justify-center space-y-8" data-id="element-394">
                    <motion.div initial={{
                    x: 40,
                    opacity: 0
                  }} animate={experienceAnimation.isVisible ? {
                    x: 0,
                    opacity: 1
                  } : {
                    x: 40,
                    opacity: 0
                  }} transition={{
                    duration: 0.8,
                    delay: 0.3,
                    ease: 'easeOut'
                  }} data-id="element-395">
                      <h3 className="text-3xl md:text-4xl font-serif mb-4 text-zinc-950" style={{
                      fontFamily: 'Playfair Display, serif'
                    }} data-id="element-396">
                        Morning Ritual
                      </h3>
                      <p className="text-zinc-600 text-lg leading-relaxed" data-id="element-397">
                        Begin each day with intention. A single dose integrated
                        seamlessly into your morning routine.
                      </p>
                    </motion.div>

                    <motion.div initial={{
                    x: 40,
                    opacity: 0
                  }} animate={experienceAnimation.isVisible ? {
                    x: 0,
                    opacity: 1
                  } : {
                    x: 40,
                    opacity: 0
                  }} transition={{
                    duration: 0.8,
                    delay: 0.5,
                    ease: 'easeOut'
                  }} data-id="element-398">
                      <h3 className="text-3xl md:text-4xl font-serif mb-4 text-zinc-950" style={{
                      fontFamily: 'Playfair Display, serif'
                    }} data-id="element-399">
                        Sustained Vitality
                      </h3>
                      <p className="text-zinc-600 text-lg leading-relaxed" data-id="element-400">
                        Experience clarity and energy that carries through your
                        most demanding moments.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="px-6 py-20 md:py-24 bg-zinc-50 text-zinc-950" ref={ctaAnimation.ref as React.RefObject<HTMLElement>} data-id="element-401">
              <motion.div className="max-w-3xl mx-auto text-center" initial={{
              y: 50,
              opacity: 0
            }} animate={ctaAnimation.isVisible ? {
              y: 0,
              opacity: 1
            } : {
              y: 50,
              opacity: 0
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} data-id="element-402">
                <h2 className="text-4xl md:text-6xl font-serif mb-8" style={{
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-403">
                  Elevate Your Cellular Health
                </h2>
                <p className="text-lg text-zinc-600 mb-12 max-w-xl mx-auto" data-id="element-404">
                  Join those who refuse to compromise on their wellness journey.
                </p>
                <motion.button onClick={() => setActiveTab('book')} className="px-12 py-4 bg-zinc-950 text-white text-sm tracking-[0.2em] uppercase font-medium hover:bg-zinc-800 transition-colors duration-300" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-405">
                  Discover NAD+
                </motion.button>
              </motion.div>
            </section>
          </>;
    }
  };
  return <div className="w-full min-h-screen bg-white" data-id="element-406">
      <AnimatePresence mode="wait" data-id="element-407">
        <motion.div key={activeTab} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-408">
          {renderContent()}
        </motion.div>
      </AnimatePresence>

      {activeTab !== 'login' && <Footer data-id="element-409" />}
    </div>;
}